import * as THREE from "three";
import type { GameSnapshot, MineState, PlayerState, ProjectileState, Vector3State } from "../shared/types";

const PROJECTILE_FORWARD = new THREE.Vector3(-1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);

const CLASSIC_FOG_COLOR = 0x003366;
const CLASSIC_WORLD_BOUNDS_XZ = 100;
const CLASSIC_SEABED_Y = -80;
const CLASSIC_SEABED_SIZE = CLASSIC_WORLD_BOUNDS_XZ * 2.5;
const CLASSIC_SEABED_SEGMENTS = 50;
const CLASSIC_THIRD_PERSON_OFFSET = new THREE.Vector3(20, 8, 0);
const CLASSIC_FIRST_PERSON_OFFSET = new THREE.Vector3(-1.5, 1, 0);
const CLASSIC_FIRST_PERSON_LOOK_AT = new THREE.Vector3(-5, 0, 0);

const ROCK_COUNT = 40;
const SEAWEED_COUNT = 70;
const PEBBLE_COUNT = 250;
const CORAL_COUNT = 35;
const SEAWEED_COLOR = 0x2e8b57;
const PEBBLE_MAX_SIZE = 0.3;
const CORAL_COLORS = [0xff4500, 0xda70d6, 0xff6347];

type PickupKind = "health" | "homing" | "mine";

interface PickupState {
  id: string;
  kind: PickupKind;
  position: Vector3State;
  radius: number;
  expiresAt: number;
}

type ClientSnapshot = GameSnapshot & { pickups?: PickupState[] };
type EventSource = "regular" | "homing" | "mine" | "pickup";

interface ImpactParticle {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
}

export class SubmarineRenderer {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  private playerMeshes = new Map<string, THREE.Group>();
  private projectileMeshes = new Map<string, THREE.Mesh>();
  private mineMeshes = new Map<string, THREE.Group>();
  private pickupMeshes = new Map<string, THREE.Group>();
  private impactParticles: ImpactParticle[] = [];
  private snapshot: ClientSnapshot | null = null;
  private localPlayerId: string | null = null;
  private firstPerson = false;
  private damagePulse = 0;
  private frame = 0;
  private seabedHeightMap: Float32Array | null = null;
  private seabedHeightMapSize = 0;
  private seabedHeightMapScale = 1;
  private seabedHalfSize = 0;

  constructor(private readonly host: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.host.appendChild(this.renderer.domElement);

    this.scene.background = new THREE.Color(CLASSIC_FOG_COLOR);
    this.scene.fog = new THREE.Fog(CLASSIC_FOG_COLOR, 80, 240);
    this.camera.position.set(24, 18, 34);

    this.addLighting();
    this.addEnvironment();
    this.resize();
    window.addEventListener("resize", this.resize);
  }

  destroy(): void {
    window.removeEventListener("resize", this.resize);
    this.renderer.dispose();
    this.host.replaceChildren();
  }

  setSnapshot(snapshot: ClientSnapshot, localPlayerId: string | null): void {
    this.snapshot = snapshot;
    this.localPlayerId = localPlayerId;
  }

  toggleFirstPerson(): boolean {
    this.firstPerson = !this.firstPerson;
    return this.firstPerson;
  }

  setFirstPerson(enabled: boolean): void {
    this.firstPerson = enabled;
  }

  spawnImpact(position: Vector3State, source: EventSource = "regular"): void {
    const origin = vectorFromState(position);
    const color = source === "homing" ? 0x00ffff : source === "mine" ? 0xffa500 : source === "pickup" ? 0xffff00 : 0xffffaa;
    const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 });
    const count = source === "mine" ? 18 : 10;
    for (let i = 0; i < count; i += 1) {
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(source === "mine" ? 0.16 : 0.1, 6, 6), material.clone());
      mesh.position.copy(origin);
      const velocity = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.8, Math.random() - 0.5)
        .normalize()
        .multiplyScalar(source === "mine" ? 0.85 : 0.5);
      this.scene.add(mesh);
      this.impactParticles.push({ mesh, velocity, life: 480, maxLife: 480 });
    }
  }

  pulseLocalDamage(): void {
    this.damagePulse = Math.max(this.damagePulse, 1);
  }

  render(): void {
    this.frame += 1;
    if (this.snapshot) {
      this.syncPlayers(this.snapshot.players);
      this.syncProjectiles(this.snapshot.projectiles);
      this.syncMines(this.snapshot.mines);
      this.syncPickups(this.snapshot.pickups ?? []);
      this.updateCamera();
    } else {
      this.camera.position.lerp(new THREE.Vector3(28, 24, 42), 0.03);
      this.camera.lookAt(0, -18, 0);
    }
    this.updateImpactParticles();
    this.renderer.render(this.scene, this.camera);
  }

  private syncPlayers(players: PlayerState[]): void {
    const liveIds = new Set(players.map((player) => player.id));
    for (const [id, mesh] of this.playerMeshes) {
      if (!liveIds.has(id)) {
        this.scene.remove(mesh);
        this.playerMeshes.delete(id);
      }
    }

    for (const player of players) {
      let mesh = this.playerMeshes.get(player.id);
      if (!mesh) {
        mesh = createSubmarineMesh(player.id === this.localPlayerId);
        this.playerMeshes.set(player.id, mesh);
        this.scene.add(mesh);
      }
      mesh.visible = player.connected && !(this.firstPerson && player.id === this.localPlayerId && player.alive);
      const target = new THREE.Vector3(player.position.x, player.position.y, player.position.z);
      mesh.position.lerp(target, 0.35);
      mesh.rotation.y = player.rotation;
      mesh.scale.setScalar(player.alive ? 1 : 0.35);
      mesh.traverse((child: THREE.Object3D) => {
        if (child instanceof THREE.Mesh) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          for (const material of materials) {
            const opacity = player.alive ? (Date.now() < player.invulnerableUntil ? 0.68 : 1) : 0.2;
            material.transparent = opacity < 1;
            material.opacity = opacity;
            material.depthWrite = opacity >= 1;
          }
        }
      });
    }
  }

  private syncProjectiles(projectiles: ProjectileState[]): void {
    const liveIds = new Set(projectiles.map((projectile) => projectile.id));
    for (const [id, mesh] of this.projectileMeshes) {
      if (!liveIds.has(id)) {
        this.scene.remove(mesh);
        this.projectileMeshes.delete(id);
      }
    }

    for (const projectile of projectiles) {
      let mesh = this.projectileMeshes.get(projectile.id);
      if (!mesh) {
        const geometry = new THREE.CapsuleGeometry(0.25, 1.5, 4, 8);
        geometry.rotateZ(Math.PI / 2);
        const material = new THREE.MeshBasicMaterial({ color: projectile.kind === "homing" ? 0x00ffff : 0xffffff });
        mesh = new THREE.Mesh(geometry, material);
        this.projectileMeshes.set(projectile.id, mesh);
        this.scene.add(mesh);
      }
      mesh.position.lerp(vectorFromState(projectile.position), 0.55);
      const direction = vectorFromState(projectile.velocity).normalize();
      if (direction.lengthSq() > 0) {
        mesh.quaternion.setFromUnitVectors(PROJECTILE_FORWARD, direction);
      }
    }
  }

  private syncMines(mines: MineState[]): void {
    const liveIds = new Set(mines.map((mine) => mine.id));
    for (const [id, mesh] of this.mineMeshes) {
      if (!liveIds.has(id)) {
        this.scene.remove(mesh);
        this.mineMeshes.delete(id);
      }
    }

    for (const mine of mines) {
      let mesh = this.mineMeshes.get(mine.id);
      if (!mesh) {
        mesh = createMineMesh();
        this.mineMeshes.set(mine.id, mesh);
        this.scene.add(mesh);
      }
      mesh.position.lerp(this.mineVisualPosition(mine.position), 0.45);
      mesh.rotation.y += 0.01;
    }
  }

  private syncPickups(pickups: PickupState[]): void {
    const liveIds = new Set(pickups.map((pickup) => pickup.id));
    for (const [id, mesh] of this.pickupMeshes) {
      if (!liveIds.has(id)) {
        this.scene.remove(mesh);
        this.pickupMeshes.delete(id);
      }
    }

    for (const pickup of pickups) {
      let mesh = this.pickupMeshes.get(pickup.id);
      if (!mesh) {
        mesh = createPickupMesh(pickup.kind);
        this.pickupMeshes.set(pickup.id, mesh);
        this.scene.add(mesh);
      }
      const bob = Math.sin(this.frame * 0.045 + pickup.position.x) * 0.24;
      mesh.position.lerp(new THREE.Vector3(pickup.position.x, pickup.position.y + bob, pickup.position.z), 0.35);
      mesh.rotation.y += 0.018;
      mesh.scale.setScalar(1);
    }
  }

  private updateCamera(): void {
    const local = this.snapshot?.players.find((player) => player.id === this.localPlayerId) ?? this.snapshot?.players[0];
    if (!local) return;
    const position = new THREE.Vector3(local.position.x, local.position.y, local.position.z);
    const shake = this.damagePulse > 0 ? new THREE.Vector3((Math.random() - 0.5) * this.damagePulse, (Math.random() - 0.5) * this.damagePulse, 0) : new THREE.Vector3();
    if (this.firstPerson && local.alive) {
      const desired = position.clone().add(yawOffset(CLASSIC_FIRST_PERSON_OFFSET, local.rotation)).add(shake);
      const lookAt = position.clone().add(yawOffset(CLASSIC_FIRST_PERSON_LOOK_AT, local.rotation));
      this.camera.position.lerp(desired, 0.32);
      this.camera.lookAt(lookAt);
    } else {
      const desired = position.clone().add(yawOffset(CLASSIC_THIRD_PERSON_OFFSET, local.rotation)).add(shake);
      this.camera.position.lerp(desired, 0.13);
      this.camera.lookAt(position);
    }
    this.damagePulse *= 0.86;
  }

  private updateImpactParticles(): void {
    for (let i = this.impactParticles.length - 1; i >= 0; i -= 1) {
      const particle = this.impactParticles[i];
      particle.life -= 16;
      particle.velocity.y -= 0.008;
      particle.mesh.position.add(particle.velocity);
      const opacity = Math.max(0, particle.life / particle.maxLife);
      const material = particle.mesh.material;
      if (material instanceof THREE.MeshBasicMaterial) {
        material.opacity = opacity;
      }
      particle.mesh.scale.setScalar(0.5 + opacity);
      if (particle.life <= 0) {
        this.scene.remove(particle.mesh);
        particle.mesh.geometry.dispose();
        if (particle.mesh.material instanceof THREE.Material) particle.mesh.material.dispose();
        this.impactParticles.splice(i, 1);
      }
    }
  }

  private addLighting(): void {
    const hemi = new THREE.HemisphereLight(0x6090ff, 0x103020, 0.8);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(20, 40, 15);
    key.castShadow = true;
    key.shadow.mapSize.width = 1024;
    key.shadow.mapSize.height = 1024;
    key.shadow.camera.near = 0.5;
    key.shadow.camera.far = 150;
    key.shadow.camera.left = -CLASSIC_WORLD_BOUNDS_XZ;
    key.shadow.camera.right = CLASSIC_WORLD_BOUNDS_XZ;
    key.shadow.camera.top = CLASSIC_WORLD_BOUNDS_XZ;
    key.shadow.camera.bottom = -CLASSIC_WORLD_BOUNDS_XZ;
    key.shadow.bias = -0.001;
    this.scene.add(key);
  }

  private addEnvironment(): void {
    const seabedMaterial = new THREE.MeshStandardMaterial({ color: 0x967969, roughness: 0.95 });
    const rockMaterialPalette = Array.from({ length: 8 }, (_, index) => {
      const color = new THREE.Color(0x777777).lerp(new THREE.Color(0x554433), (index / 8) * 0.4);
      return new THREE.MeshStandardMaterial({ color, roughness: 0.8 + (index % 3) * 0.05 });
    });
    const pebbleMaterial1 = new THREE.MeshStandardMaterial({ color: 0x665544, roughness: 0.9 });
    const pebbleMaterial2 = new THREE.MeshStandardMaterial({ color: 0x887766, roughness: 0.9 });
    const seaweedMaterial = new THREE.MeshStandardMaterial({ color: SEAWEED_COLOR, roughness: 0.7, metalness: 0.1 });
    const coralMaterials = CORAL_COLORS.map((color) => new THREE.MeshStandardMaterial({ color, roughness: 0.6, metalness: 0.1 }));
    const shipwreckMaterial = new THREE.MeshStandardMaterial({ color: 0x5a4d41, roughness: 0.8, metalness: 0.2 });

    this.createSeabed(seabedMaterial);
    this.addRocks(rockMaterialPalette);
    this.addPebbles(pebbleMaterial1, pebbleMaterial2);
    this.addSeaweed(seaweedMaterial);
    this.addCoral(coralMaterials);
    this.addShipwreck(shipwreckMaterial);
  }

  private createSeabed(material: THREE.Material): void {
    const geometry = new THREE.PlaneGeometry(CLASSIC_SEABED_SIZE, CLASSIC_SEABED_SIZE, CLASSIC_SEABED_SEGMENTS, CLASSIC_SEABED_SEGMENTS);
    const positions = geometry.attributes.position as THREE.BufferAttribute;
    const gridSize = CLASSIC_SEABED_SEGMENTS + 1;
    this.seabedHeightMap = new Float32Array(gridSize * gridSize);
    this.seabedHeightMapSize = gridSize;
    this.seabedHeightMapScale = CLASSIC_SEABED_SIZE / CLASSIC_SEABED_SEGMENTS;
    this.seabedHalfSize = CLASSIC_SEABED_SIZE / 2;

    for (let i = 0; i < positions.count; i += 1) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const noise = (Math.sin(x * 0.08) + Math.cos(y * 0.08)) * 1.2 + (Math.random() - 0.5) * 0.7;
      positions.setZ(i, noise);
      this.seabedHeightMap[i] = CLASSIC_SEABED_Y + noise;
    }

    geometry.computeVertexNormals();
    const seabed = new THREE.Mesh(geometry, material);
    seabed.rotation.x = -Math.PI / 2;
    seabed.position.y = CLASSIC_SEABED_Y;
    seabed.receiveShadow = true;
    this.scene.add(seabed);
  }

  private getSeabedHeight(worldX: number, worldZ: number): number {
    if (!this.seabedHeightMap) return CLASSIC_SEABED_Y;
    const gridX = (worldX + this.seabedHalfSize) / this.seabedHeightMapScale;
    const gridZ = (worldZ + this.seabedHalfSize) / this.seabedHeightMapScale;
    const x0 = Math.max(0, Math.min(this.seabedHeightMapSize - 2, Math.floor(gridX)));
    const z0 = Math.max(0, Math.min(this.seabedHeightMapSize - 2, Math.floor(gridZ)));
    const x1 = x0 + 1;
    const z1 = z0 + 1;
    const fx = gridX - x0;
    const fz = gridZ - z0;
    const h00 = this.seabedHeightMap[z0 * this.seabedHeightMapSize + x0];
    const h10 = this.seabedHeightMap[z0 * this.seabedHeightMapSize + x1];
    const h01 = this.seabedHeightMap[z1 * this.seabedHeightMapSize + x0];
    const h11 = this.seabedHeightMap[z1 * this.seabedHeightMapSize + x1];
    const h0 = h00 * (1 - fx) + h10 * fx;
    const h1 = h01 * (1 - fx) + h11 * fx;
    return h0 * (1 - fz) + h1 * fz;
  }

  private mineVisualPosition(position: Vector3State): THREE.Vector3 {
    const seabedClearance = this.getSeabedHeight(position.x, position.z) + 2;
    return new THREE.Vector3(position.x, Math.max(position.y - 2, seabedClearance), position.z);
  }

  private addRocks(materials: THREE.Material[]): void {
    const geometries = [new THREE.DodecahedronGeometry(1), new THREE.IcosahedronGeometry(1), new THREE.TetrahedronGeometry(1)];
    for (let i = 0; i < ROCK_COUNT; i += 1) {
      const geometry = geometries[Math.floor(Math.random() * geometries.length)];
      const material = materials[Math.floor(Math.random() * materials.length)];
      const rock = new THREE.Mesh(geometry, material);
      const scale = 1.5 + Math.random() * 3.5;
      const x = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 2;
      const z = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 2;
      rock.scale.set(scale, scale, scale);
      rock.position.set(x, this.getSeabedHeight(x, z) + scale * 0.3, z);
      rock.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      rock.castShadow = true;
      rock.receiveShadow = true;
      this.scene.add(rock);
    }
  }

  private addPebbles(material1: THREE.Material, material2: THREE.Material): void {
    const geometry = new THREE.SphereGeometry(1, 6, 4);
    for (let i = 0; i < PEBBLE_COUNT; i += 1) {
      const pebble = new THREE.Mesh(geometry, Math.random() < 0.5 ? material1 : material2);
      const scale = PEBBLE_MAX_SIZE * (0.3 + Math.random() * 0.7);
      const x = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 2;
      const z = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 2;
      pebble.scale.set(scale, scale, scale);
      pebble.position.set(x, CLASSIC_SEABED_Y + scale * 0.5, z);
      pebble.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      pebble.castShadow = false;
      pebble.receiveShadow = true;
      this.scene.add(pebble);
    }
  }

  private addSeaweed(material: THREE.Material): void {
    const cylinderGeometry = new THREE.CylinderGeometry(0.1, 0.15, 5, 5);
    const flatGeometry = new THREE.BoxGeometry(0.1, 5, 0.8);
    for (let i = 0; i < SEAWEED_COUNT; i += 1) {
      const clumpSize = 3 + Math.floor(Math.random() * 5);
      const baseX = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 1.8;
      const baseZ = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 1.8;
      for (let j = 0; j < clumpSize; j += 1) {
        const seaweed = new THREE.Mesh(Math.random() < 0.4 ? flatGeometry : cylinderGeometry, material);
        const height = 3 + Math.random() * 4;
        const x = baseX + (Math.random() - 0.5) * 1.5;
        const z = baseZ + (Math.random() - 0.5) * 1.5;
        seaweed.scale.y = height / 5;
        seaweed.position.set(x, this.getSeabedHeight(x, z) + height / 2, z);
        seaweed.rotation.x = (Math.random() - 0.5) * 0.2;
        seaweed.rotation.z = (Math.random() - 0.5) * 0.2;
        seaweed.rotation.y = Math.random() * Math.PI;
        seaweed.castShadow = true;
        this.scene.add(seaweed);
      }
    }
  }

  private addCoral(materials: THREE.Material[]): void {
    const branchGeometry = new THREE.CylinderGeometry(0.1, 0.05, 1.5, 8);
    const baseGeometry = new THREE.SphereGeometry(0.8, 8, 6);
    for (let i = 0; i < CORAL_COUNT; i += 1) {
      const material = materials[Math.floor(Math.random() * materials.length)];
      const coral = new THREE.Group();
      const base = new THREE.Mesh(baseGeometry, material);
      coral.add(base);

      const branches = 3 + Math.floor(Math.random() * 5);
      for (let j = 0; j < branches; j += 1) {
        const branch = new THREE.Mesh(branchGeometry, material);
        const scale = 1.5 + Math.random() * 1.8;
        branch.scale.set(scale, scale, scale);
        branch.position.y = scale * 0.75;
        branch.rotation.x = (Math.random() - 0.5) * Math.PI * 0.8;
        branch.rotation.z = (Math.random() - 0.5) * Math.PI * 0.8;
        branch.rotation.y = Math.random() * Math.PI * 2;
        coral.add(branch);
      }

      const x = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 1.5;
      const z = (Math.random() - 0.5) * CLASSIC_WORLD_BOUNDS_XZ * 1.5;
      coral.position.set(x, this.getSeabedHeight(x, z) + 0.1, z);
      coral.rotation.y = Math.random() * Math.PI * 2;
      coral.castShadow = true;
      coral.receiveShadow = true;
      this.scene.add(coral);
    }
  }

  private addShipwreck(material: THREE.Material): void {
    const shipwreck = createShipwreckMesh(material);
    const x = 15 + (Math.random() - 0.5) * 30;
    const z = -20 + (Math.random() - 0.5) * 30;
    shipwreck.position.set(x, this.getSeabedHeight(x, z) + 0.2, z);
    shipwreck.rotation.y = Math.random() * Math.PI * 2;
    shipwreck.rotation.x = (Math.random() - 0.5) * 0.3;
    shipwreck.rotation.z = (Math.random() - 0.5) * 0.3;
    this.scene.add(shipwreck);
  }

  private resize = (): void => {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };
}

function createSubmarineMesh(local: boolean): THREE.Group {
  const group = new THREE.Group();
  const radius = local ? 1.3 : 1.0;
  const length = local ? 6.5 : 5.0;
  const towerRadius = local ? 0.65 : 0.5;
  const towerHeight = local ? 1.95 : 1.3;
  const bodyColor = local ? 0xffd700 : 0xff6347;
  const towerColor = local ? 0xffff00 : 0xff6347;
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: bodyColor,
    metalness: local ? 0.3 : 0.2,
    roughness: local ? 0.6 : 0.7,
    transparent: false
  });
  const towerMaterial = new THREE.MeshStandardMaterial({
    color: towerColor,
    metalness: local ? 0.3 : 0.2,
    roughness: local ? 0.6 : 0.7,
    transparent: false
  });

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(radius, length, 4, local ? 10 : 8), bodyMaterial);
  body.rotation.z = Math.PI / 2;
  body.castShadow = true;
  group.add(body);

  const tower = new THREE.Mesh(new THREE.CylinderGeometry(towerRadius, towerRadius, towerHeight, local ? 16 : 12), towerMaterial);
  tower.position.y = radius + towerHeight / 2 - (local ? 0.25 : 0.15);
  tower.castShadow = true;
  group.add(tower);

  return group;
}

function createMineMesh(): THREE.Group {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0xffa500, metalness: 0.6, roughness: 0.5 });
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.6, 12, 8), material);
  group.add(core);

  for (let i = 0; i < 10; i += 1) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.4, 6), material);
    const phi = Math.acos(-1 + (2 * i) / 10);
    const theta = Math.sqrt(10 * Math.PI) * phi;
    spike.position.setFromSphericalCoords(0.6 + 0.4 * 0.4, phi, theta);
    spike.lookAt(0, 0, 0);
    spike.rotateX(Math.PI / 2);
    group.add(spike);
  }

  group.scale.setScalar(2);
  return group;
}

function createPickupMesh(kind: PickupKind): THREE.Group {
  const group = new THREE.Group();
  if (kind === "health") {
    group.add(new THREE.Mesh(new THREE.IcosahedronGeometry(1.8), new THREE.MeshBasicMaterial({ color: 0x00bfff, wireframe: true })));
  } else if (kind === "homing") {
    group.add(new THREE.Mesh(new THREE.OctahedronGeometry(2), new THREE.MeshBasicMaterial({ color: 0x00ff00, wireframe: true })));
  } else {
    group.add(new THREE.Mesh(new THREE.SphereGeometry(1.6, 8, 6), new THREE.MeshBasicMaterial({ color: 0xffff00, wireframe: true })));
  }
  return group;
}

function createShipwreckMesh(material: THREE.Material): THREE.Group {
  const group = new THREE.Group();
  const hullRadius = 3;
  const hullLength = 25;
  const mastHeight = hullRadius * 3;

  const hull = new THREE.Mesh(new THREE.CapsuleGeometry(hullRadius, hullLength, 4, 10), material);
  hull.rotation.z = Math.PI / 2;
  hull.rotation.x = 0.1;
  hull.position.y = hullRadius * 0.8;
  hull.castShadow = true;
  hull.receiveShadow = true;
  group.add(hull);

  const deckHouse = new THREE.Mesh(new THREE.BoxGeometry(6, 4, 5), material);
  deckHouse.position.set(hullLength * 0.1, hullRadius * 1.2, 0);
  deckHouse.rotation.y = 0.2;
  deckHouse.rotation.z = -0.1;
  deckHouse.castShadow = true;
  deckHouse.receiveShadow = true;
  group.add(deckHouse);

  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.4, mastHeight, 8), material);
  mast.position.set(-hullLength * 0.3, hullRadius * 0.6, -hullRadius * 0.5);
  mast.rotation.z = (Math.PI / 2) * 0.9;
  mast.rotation.x = 0.4;
  mast.castShadow = true;
  mast.receiveShadow = true;
  group.add(mast);

  return group;
}

function yawOffset(offset: THREE.Vector3, rotation: number): THREE.Vector3 {
  return offset.clone().applyAxisAngle(Y_AXIS, rotation);
}

function vectorFromState(vector: Vector3State): THREE.Vector3 {
  return new THREE.Vector3(vector.x, vector.y, vector.z);
}
